import React, { useState, useEffect, useCallback, useMemo } from 'react';
import ReactQuill from 'react-quill';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  Typography,
  InputAdornment,
  Chip,
  LinearProgress,
  Tabs,
  Tab,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ImageIcon from '@mui/icons-material/Image';
import DescriptionIcon from '@mui/icons-material/Description';
import LinkIcon from '@mui/icons-material/Link';
import FolderIcon from '@mui/icons-material/Folder';
import {
  DocumentsClient,
  DocumentFoldersClient,
  DocumentDto,
  DocumentFolderDto,
  formatFileSize,
} from '../../app/services/api/documentsClient';
import { DocumentType, DocumentScope } from '../../enums/DocumentPermission';

const documentsClient = new DocumentsClient();
const foldersClient = new DocumentFoldersClient();

interface RichTextEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  height?: number;
  readOnly?: boolean;
  /** Enable document linking feature */
  enableDocumentLinking?: boolean;
  allowImages?: boolean;
  /** Franchise ID for filtering documents */
  franchiseId?: number;
  /** Store ID for filtering store-specific documents */
  storeId?: number;
}

/**
 * Rich text editor with markdown-style document linking capability.
 * Supports linking to documents from the document management system
 * using a special syntax: [Document Name](doc:documentId)
 */
const RichTextEditor: React.FC<RichTextEditorProps> = ({
  value,
  onChange,
  placeholder = 'Enter content...',
  height = 300,
  readOnly = false,
  enableDocumentLinking = true,
  allowImages = true,
  franchiseId,
  storeId,
}) => {
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [documents, setDocuments] = useState<DocumentDto[]>([]);
  const [folders, setFolders] = useState<DocumentFolderDto[]>([]);
  const [currentFolderId, setCurrentFolderId] = useState<number | null>(null);
  const [currentFolder, setCurrentFolder] = useState<DocumentFolderDto | null>(null);
  const [loading, setLoading] = useState(false);
  const [tabValue, setTabValue] = useState(0);

  // Quill modules configuration
  const modules = useMemo(() => ({
    toolbar: {
      container: [
        [{ 'header': [1, 2, 3, false] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ 'list': 'ordered' }, { 'list': 'bullet' }],
        [{ 'indent': '-1' }, { 'indent': '+1' }],
        allowImages ? ['link', 'image'] : ['link'],
        ['clean'],
      ],
    },
  }), [allowImages]);

  const formats = [
    'header',
    'bold', 'italic', 'underline', 'strike',
    'list', 'bullet', 'indent',
    'link', ...(allowImages ? ['image'] : []),
  ];

  // Load documents for the link dialog
  const loadDocuments = useCallback(async () => {
    setLoading(true);
    try {
      if (tabValue === 0) {
        // Search mode
        if (searchQuery.trim()) {
          const result = await documentsClient.searchDocuments({
            query: searchQuery,
            franchiseId,
            pageSize: 20,
          });
          setDocuments(result.documents);
          setFolders([]);
        } else {
          setDocuments([]);
        }
      } else {
        // Browse mode
        if (currentFolderId) {
          const [childFolders, docs] = await Promise.all([
            foldersClient.getChildFolders(currentFolderId),
            documentsClient.getDocuments(currentFolderId),
          ]);
          setFolders(childFolders);
          setDocuments(docs);
          const folder = await foldersClient.getFolder(currentFolderId);
          setCurrentFolder(folder);
        } else {
          const rootFolders = await foldersClient.getRootFolders(
            undefined,
            franchiseId,
            storeId,
          );
          setFolders(rootFolders);
          setDocuments([]);
          setCurrentFolder(null);
        }
      }
    } catch (error) {
      console.error('Error loading documents:', error);
    } finally {
      setLoading(false);
    }
  }, [searchQuery, tabValue, currentFolderId, franchiseId, storeId]);

  useEffect(() => {
    if (linkDialogOpen) {
      loadDocuments();
    }
  }, [linkDialogOpen, loadDocuments]);

  const getDocumentIcon = (documentType: DocumentType) => {
    switch (documentType) {
      case DocumentType.PDF:
        return <PictureAsPdfIcon color="error" />;
      case DocumentType.Image:
        return <ImageIcon color="primary" />;
      case DocumentType.Document:
        return <DescriptionIcon color="info" />;
      default:
        return <InsertDriveFileIcon />;
    }
  };

  const handleOpenLinkDialog = () => {
    setSearchQuery('');
    setCurrentFolderId(null);
    setCurrentFolder(null);
    setTabValue(0);
    setLinkDialogOpen(true);
  };

  const handleFolderClick = (folder: DocumentFolderDto) => {
    setCurrentFolderId(folder.id);
  };

  const handleBackClick = () => {
    if (currentFolder?.parentFolderId) {
      setCurrentFolderId(currentFolder.parentFolderId);
    } else {
      setCurrentFolderId(null);
    }
  };

  const handleDocumentSelect = (document: DocumentDto) => {
    // Insert a document link in markdown-style format
    // Format: [Document Name](doc:documentId)
    const documentLink = `<a href="/documents/view/${document.id}" class="document-link" data-doc-id="${document.id}">${document.name}</a>`;

    // Insert at current cursor position or append
    const newValue = value + documentLink + ' ';
    onChange(newValue);
    setLinkDialogOpen(false);
  };

  const getScopeLabel = (scope: DocumentScope) => {
    switch (scope) {
      case DocumentScope.Franchise:
        return 'Franchise';
      case DocumentScope.Store:
        return 'Store';
      case DocumentScope.TaskEvidence:
        return 'Evidence';
      default:
        return scope;
    }
  };

  return (
    <Box sx={{ position: 'relative' }}>
      {/* Custom toolbar button for document linking */}
      {enableDocumentLinking && !readOnly && (
        <Box
          sx={{
            position: 'absolute',
            top: '4px',
            right: '4px',
            zIndex: 10,
          }}
        >
          <Button
            size="md"
            variant="secondary"
            startIcon={<LinkIcon />}
            onClick={handleOpenLinkDialog}
          >
            Link Document
          </Button>
        </Box>
      )}

      {/* Quill Editor */}
      <ReactQuill
        theme="snow"
        value={value}
        onChange={onChange}
        modules={modules}
        formats={formats}
        placeholder={placeholder}
        readOnly={readOnly}
        style={{ height, borderRadius: 4, marginBottom: 10 }}
      />

      {/* Document Link Dialog */}
      <Dialog
        open={linkDialogOpen}
        onClose={() => setLinkDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <LinkIcon />
            Link Document
          </Box>
        </DialogTitle>
        <DialogContent>
          <Tabs
            value={tabValue}
            onChange={(_, newValue) => {
              setTabValue(newValue);
              setSearchQuery('');
              setCurrentFolderId(null);
            }}
            sx={{ mb: 2 }}
          >
            <Tab label="Search" />
            <Tab label="Browse" />
          </Tabs>

          {tabValue === 0 && (
            <TextField
              fullWidth
              placeholder="Search documents..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              sx={{ mb: 2 }}
            />
          )}

          {tabValue === 1 && currentFolder && (
            <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <Button
                size="md"
                onClick={handleBackClick}
              >
                ← Back
              </Button>
              <Typography color="text.secondary">
                {currentFolder.path.join(' / ')}
              </Typography>
            </Box>
          )}

          {loading && <LinearProgress sx={{ mb: 2 }} />}

          <List sx={{ maxHeight: 400, overflow: 'auto' }}>
            {/* Folders */}
            {folders.map((folder) => (
              <ListItemButton
                key={`folder-${folder.id}`}
                onClick={() => handleFolderClick(folder)}
              >
                <ListItemIcon>
                  <FolderIcon color="warning" />
                </ListItemIcon>
                <ListItemText
                  primary={folder.name}
                  secondary={`${folder.childFolderCount} folders, ${folder.documentCount} files`}
                />
                <Chip
                  size="small"
                  label={getScopeLabel(folder.scope)}
                  sx={{ ml: 1 }}
                />
              </ListItemButton>
            ))}

            {/* Documents */}
            {documents.map((doc) => (
              <ListItemButton
                key={`doc-${doc.id}`}
                onClick={() => handleDocumentSelect(doc)}
              >
                <ListItemIcon>
                  {getDocumentIcon(doc.latestVersion.documentType)}
                </ListItemIcon>
                <ListItemText
                  primary={doc.name}
                  secondary={
                    <Box
                      component="span"
                      sx={{ display: 'flex', gap: 1, alignItems: 'center' }}
                    >
                      {doc.description && (
                        <Typography variant="textXS">{doc.description}</Typography>
                      )}
                      <Typography
                        variant="textXS"
                        color="text.secondary"
                      >
                        {formatFileSize(doc.latestVersion.fileSize)}
                      </Typography>
                    </Box>
                  }
                />
                {doc.tags && doc.tags.length > 0 && (
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    {doc.tags.slice(0, 2).map((tag) => (
                      <Chip key={tag}
                        size="small"
                        label={tag}
                        variant="outlined" />
                    ))}
                  </Box>
                )}
              </ListItemButton>
            ))}

            {!loading && folders.length === 0 && documents.length === 0 && (
              <ListItem>
                <ListItemText
                  primary={
                    <Typography color="text.secondary"
                      align="center">
                      {tabValue === 0
                        ? 'Enter a search term to find documents'
                        : 'No documents in this folder'}
                    </Typography>
                  }
                />
              </ListItem>
            )}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLinkDialogOpen(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>

      {/* Styles for document links */}
      <style>{`
        .ql-container {
          font-size: 14px;
          height: auto;
          margin-bottom: 10px;
        }
        .document-link {
          color: #1976d2;
          text-decoration: none;
          background-color: #e3f2fd;
          padding: 2px 6px;
          border-radius: 4px;
          font-weight: 500;
        }
        .document-link:hover {
          background-color: #bbdefb;
        }
        .ql-editor {
          min-height: ${height - 42}px;
        }
        .ql-toolbar.ql-snow{
          border-radius: 4px 4px 0 0;
        }
        .ql-toolbar.ql-snow + .ql-container.ql-snow{
          border-radius: 0 0 4px 4px;
        }
      `}</style>
    </Box>
  );
};

export default RichTextEditor;
